import { motion, AnimatePresence } from "motion/react";
import { X, ZoomIn, Download } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useState } from "react";

interface ImageGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: { url: string; label: string; timestamp?: string }[];
  title: string;
}

export default function ImageGalleryModal({
  isOpen,
  onClose,
  images,
  title,
}: ImageGalleryModalProps) {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet Modal */}
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 pointer-events-none pb-16 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="bg-white rounded-t-3xl md:rounded-2xl w-full md:max-w-2xl max-h-[85vh] md:max-h-[85vh] overflow-hidden pointer-events-auto mb-0 md:mb-0"
              style={{ boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.15), 0 20px 50px rgba(0, 0, 0, 0.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle (Mobile Only) */}
              <div className="md:hidden flex justify-center py-3 bg-white">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div>
                  <h2 className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                    {title}
                  </h2>
                  <p className="text-slate-500" style={{ fontSize: "14px" }}>
                    {images.length} {images.length === 1 ? "Image" : "Images"}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </motion.button>
              </div>

              {/* Content with bottom padding for nav bar */}
              <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 140px)", paddingBottom: "80px" }}>
                <div className="p-5 space-y-4">
                  {images.map((image, index) => (
                    <div key={index} className="bg-slate-50 rounded-xl overflow-hidden">
                      <div className="relative group">
                        <ImageWithFallback
                          src={image.url}
                          alt={image.label}
                          className="w-full h-64 object-cover cursor-pointer"
                          onClick={() => setSelectedImage(index)}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                          <motion.div
                            initial={{ scale: 0 }}
                            whileHover={{ scale: 1 }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <div className="bg-white rounded-full p-3 shadow-lg">
                              <ZoomIn className="w-6 h-6 text-slate-700" />
                            </div>
                          </motion.div>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-slate-900 mb-1" style={{ fontWeight: "600" }}>
                              {image.label}
                            </div>
                            {image.timestamp && (
                              <div className="text-slate-500" style={{ fontSize: "13px" }}>
                                {image.timestamp}
                              </div>
                            )}
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Full Image Preview */}
          {selectedImage !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl w-full"
              >
                <ImageWithFallback
                  src={images[selectedImage].url}
                  alt={images[selectedImage].label}
                  className="w-full h-auto rounded-xl"
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(null);
                  }}
                  className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg"
                >
                  <X className="w-5 h-5 text-slate-900" />
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}